import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { logAuditEvent } from "@/hooks/useAuditLogs";

export interface Profile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  token_balance: number;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isAdmin: boolean;
  loading: boolean;
  signUp: (email: string, password: string, name?: string, phone?: string) => Promise<{ data: { user: User | null } | null; error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data: profileData } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .single();
    
    if (profileData) {
      setProfile(profileData);
    }

    // Check if user is admin
    const { data: roleData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .single();
    
    setIsAdmin(!!roleData);
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  useEffect(() => {
    let isMounted = true;
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Use setTimeout to avoid potential Supabase deadlock
          setTimeout(async () => {
            if (isMounted) {
              await fetchProfile(session.user.id);
              if (isMounted) setLoading(false);
            }
          }, 0);
        } else {
          setProfile(null);
          setIsAdmin(false);
          setLoading(false);
        }
      }
    );

    // THEN check for existing session
    const initSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!isMounted) return;
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        await fetchProfile(session.user.id);
      }
      if (isMounted) setLoading(false);
    };
    
    initSession();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, name?: string, phone?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: name,
          phone: phone,
        }
      }
    });

    if (!error && data.user) {
      // Update profile with name and phone
      const nameParts = name?.split(" ") || [];
      await supabase
        .from("profiles")
        .update({
          first_name: nameParts[0] || null,
          last_name: nameParts.slice(1).join(" ") || null,
          phone: phone || null,
        })
        .eq("user_id", data.user.id);
      
      // Log successful signup
      logAuditEvent({
        eventCategory: 'authentication',
        eventType: 'user.signup',
        eventSeverity: 'info',
        resourceType: 'user',
        resourceId: data.user.id,
        resourceName: email,
        action: 'CREATE',
        description: `New user account created: ${email}`,
        metadata: { name, hasPhone: !!phone },
      });
    } else if (error) {
      // Log failed signup attempt
      logAuditEvent({
        eventCategory: 'authentication',
        eventType: 'user.signup_failed',
        eventSeverity: 'warning',
        action: 'CREATE',
        description: `Failed signup attempt for: ${email}`,
        metadata: { error: error.message },
      });
    }

    return { data: data.user ? { user: data.user } : null, error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (!error && data.user) {
      // Log successful login
      logAuditEvent({
        eventCategory: 'authentication',
        eventType: 'user.login',
        eventSeverity: 'info',
        resourceType: 'user',
        resourceId: data.user.id,
        resourceName: email,
        action: 'LOGIN',
        description: `User logged in: ${email}`,
      });
    } else if (error) {
      // Log failed login attempt
      logAuditEvent({
        eventCategory: 'authentication',
        eventType: 'user.login_failed',
        eventSeverity: 'warning',
        action: 'LOGIN',
        description: `Failed login attempt for: ${email}`,
        metadata: { error: error.message },
      });
    }
    
    return { error: error as Error | null };
  };

  const signOut = async () => {
    const currentUser = user;
    const currentEmail = profile?.email;
    
    await supabase.auth.signOut();
    
    // Log logout event
    if (currentUser) {
      logAuditEvent({
        eventCategory: 'authentication',
        eventType: 'user.logout',
        eventSeverity: 'info',
        resourceType: 'user',
        resourceId: currentUser.id,
        resourceName: currentEmail || undefined,
        action: 'LOGOUT',
        description: `User logged out: ${currentEmail || currentUser.id}`,
      });
    }
    
    setUser(null);
    setSession(null);
    setProfile(null);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        isAdmin,
        loading,
        signUp,
        signIn,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
